import React from 'react'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import uuid from 'uuid'
import withStyles from '@material-ui/core/styles/withStyles'
import TableRow from '../../../../uiKit/table/TableRow'
import TableCell from '../../../../uiKit/table/TableCell'
import Table from '../../../../uiKit/table/Table'
import Select from 'react-select'
import DeleteAccess from './DeleteAccess'
import Button from '@material-ui/core/Button'
import Modal from '../../../../uiKit/Modal'
import { SearchIcon } from '../../../../uiKit/icons/Icons'
import ShareModal from '../../../home/components/ShareModal'
import {
  loadBotAccessors,
  removeBotAccessor,
  updateBotAccessorRole,
  updateBotAccessorSendEmailNotifications,
} from '../../api/settings'
import {
  ROLE,
  ROLE_OPTIONS,
  hasPermissionForFeature,
  getAvailableRoles,
  hasPermissionForFeatureByRole,
} from '../../../../security'
import Heading from '../../../../uiKit/texts/Heading'
import { Checkbox, FormControlLabel } from '@material-ui/core'
import { customStyles } from '../../../../uiKit/AtomSelect/styles'
import { isQcsBot } from '../../../../constants/qcs'
import { resolveQcsLandingTab } from '../../../home/api/usageLimits'
import { isNotError } from '../../../authorization/actions/forbiddenError'
import { HOME_PATH } from '../../../../configs'
import Loader from '../../../../uiKit/loaders/loader'

const baseUserImage = '/images/platform/default-user-icon.svg'
const sendEmailNotificationsTooltipText =
  'Send email notifications about new customer support requests ' + 'and AI training completion.'

// Same silhouette as default-user-icon.svg, but fill follows --careco-primary so
// QCS avatars are pine. CareBrain keeps the baked-in cyan SVG asset.
const DefaultUserIcon = ({ className }) => (
  <svg
    className={className}
    width="40"
    height="40"
    viewBox="0 0 37 38"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true">
    <path
      d="M29.9708 25.6069L24.4304 24.4973C23.8426 24.3808 23.4145 23.8589 23.4145 23.256V21.9843C23.7793 21.4776 24.1213 20.8012 24.476 20.0944C24.747 19.5599 25.1549 18.7543 25.3373 18.5643C26.4114 17.5129 27.5565 16.2083 27.9061 14.5084C28.2228 12.9681 27.9061 12.1676 27.5388 11.5343C27.5388 10.2955 27.5388 8.02813 27.1081 6.39413C27.065 4.42067 26.7028 3.306 25.7933 2.31293C25.1498 1.6188 24.2024 1.45667 23.4424 1.32747C23.1409 1.2768 22.728 1.20587 22.5836 1.12733C21.2688 0.400267 19.9337 0.0405333 18.2997 0C14.9557 0.126667 10.8517 2.24707 9.47864 6.05973C9.06064 7.21747 9.10117 9.11747 9.13157 10.6451L9.0961 11.5343C8.77437 12.1676 8.4577 12.9681 8.7769 14.5059C9.1265 16.2133 10.2716 17.5155 11.3406 18.5617C11.5256 18.7543 11.9385 19.5649 12.2121 20.0995C12.5718 20.8037 12.9164 21.4801 13.2812 21.9868V23.256C13.2812 23.8589 12.853 24.3808 12.2628 24.4973L6.72237 25.6069C3.1833 26.3137 0.614502 29.4475 0.614502 33.0575V35.4667C0.614502 36.8625 1.75197 38 3.14784 38H33.5478C34.9437 38 36.0812 36.8625 36.0812 35.4667V33.0575C36.0812 29.4475 33.5124 26.3137 29.9708 25.6069Z"
      fill="var(--careco-primary)"
    />
  </svg>
)

const styles = theme => ({
  inputHolder: {
    border: '1px solid var(--color-ghost-grey)',
    width: 'max-content',
    borderRadius: '10px',
    display: 'flex',
    justifyContent: 'space-between',
    backgroundColor: 'var(--color-white)',
    marginRight: 10,
    padding: 10,
    maxHeight: 45,
    minWidth: 340,
  },
  input: {
    width: '100%',
    padding: '10px',
    border: 'none',
    outline: 'none',
    borderRadius: '5px',
    fontSize: '14px',
    lineHeight: '19px',
    backgroundColor: 'var(--color-white)',
  },
  submitButton: {
    backgroundColor: 'var(--color-button-primary)',
    boxShadow: '0px 3px 9px rgba(19, 69, 186, 0.206267)',
    borderRadius: 10,
    height: 45,
    outline: 'none',
    '&:hover': {
      backgroundColor: 'var(--color-button-primary)',
    },
  },
  submitButtonText: {
    color: 'var(--color-text-on-primary)',
    margin: 0,
    fontWeight: 500,
    fontSize: 16,
    textTransform: 'none',
    fontFamily: 'Lato, sans-serif',
  },
  paper: {
    padding: '30px 50px 24px',
  },
  tableCellData: {
    margin: 'auto 0',
  },
  pendingInvite: {
    display: 'block',
    marginTop: 2,
    fontSize: 12,
    lineHeight: '16px',
    color: 'var(--color-text-secondary)',
    fontStyle: 'italic',
  },
  userImageContainer: {
    height: 40,
    flex: '0 0 40px',
    position: 'relative',
    marginRight: 8,
  },
  userImage: {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'top',
    borderRadius: '50%',
    overflow: 'hidden',
  },
  userStatus: {
    position: 'absolute',
    boxSizing: 'border-box',
    width: 10,
    height: 10,
    border: '2px solid var(--color-white)',
    borderRadius: '50%',
    bottom: 0,
    right: 0,
  },
})

function compareBotAccessors(first, second) {
  if (first.isInvited === second.isInvited) {
    return compareAccessorsId(first, second)
  }
  return compareAccessorsIsInvited(first)
}

function compareAccessorsId(first, second) {
  return first.id - second.id // Sort by id
}

function compareAccessorsIsInvited(first) {
  return first.isInvited ? 1 : -1 // false first
}

function getUserStatusColor(accessor) {
  if (accessor.active) {
    return 'var(--color-malachite-green)'
  }
  return 'var(--color-spun-pearl-grey)'
}

class Users extends React.Component {
  state = {
    open: false,
    searchUser: '',
    archivedStatus: '',
    accessorsLoading: true,
    updatingAccessorKey: null,
  }
  handleArchiveStatusChange = archivedStatus => {
    this.setState({ archivedStatus, accessorsLoading: true })
    loadBotAccessors(this.props.match.params.botId, archivedStatus).finally(() => {
      if (this.mounted) this.setState({ accessorsLoading: false })
    })
  }

  removeAccess = (id, inviteAccessId) => {
    removeBotAccessor(this.props.match.params.botId, id, inviteAccessId)
  }
  closeShareModal = () => {
    this.setState({ open: false })
  }

  componentDidMount() {
    this.mounted = true
    loadBotAccessors(this.props.match.params.botId).finally(() => {
      if (this.mounted) this.setState({ accessorsLoading: false })
    })
  }

  componentWillUnmount() {
    this.mounted = false
  }

  handleSearchUser(value) {
    this.setState({ searchUser: value })
  }

  updateRole(id, inviteAccessId, newRole, accessor) {
    const botId = this.props.match.params.botId
    const isSelf = accessor?.adminId != null && accessor.adminId === this.props.adminUser?.id
    const losesSettings = isSelf && !hasPermissionForFeatureByRole('settings', newRole)
    const key = id != null ? id : inviteAccessId
    this.setState({ updatingAccessorKey: key })
    updateBotAccessorRole(botId, id, inviteAccessId, newRole, { skipListReload: losesSettings })
      .then(result => {
        if (!result?.skippedListReload || !this.mounted) return
        isNotError()
        const isQcs = isQcsBot(this.props.activeBot || botId)
        const tab = isQcs ? resolveQcsLandingTab(newRole, this.props.activeBot) : 'dashboard'
        this.props.history.replace(`${HOME_PATH}/bot/${botId}/${tab}`)
      })
      .finally(() => {
        if (this.mounted) this.setState({ updatingAccessorKey: null })
      })
  }

  updateSupportNotifying(id, sendEmailNotifications) {
    updateBotAccessorSendEmailNotifications(this.props.match.params.botId, id, sendEmailNotifications)
  }

  render() {
    //NOSONAR
    const botAccessors = [...this.props.botAccessors]
    const { classes } = this.props
    const shouldDisplayTableContents = botAccessors?.length
    const canShareBot = hasPermissionForFeatureByRole('shareBot', getAdminRole(botAccessors, this.props.adminUser))
    const isQcs = isQcsBot(this.props.activeBot || this.props.match.params.botId)
    const showSendNotifications = !isQcs
    const titles = [
      { title: 'Full name', maxWidth: 210 },
      { title: 'Email', maxWidth: 210 },
      { title: 'Role', maxWidth: 150 },
    ]
    if (showSendNotifications) {
      titles.push({
        title: 'Send notifications',
        tooltipText: sendEmailNotificationsTooltipText,
        maxWidth: 100,
      })
    }
    if (!isQcs) titles.push('Action')

    function getAdminRole(botAccessors, adminUser) {
      return botAccessors.map(botAccessor => adminUser.botAccesses.find(botAccess => botAccess.botId === botAccessor.botId)?.role)[0]
    }

    function isMatchingSearch(ba, searchUser) {
      const term = searchUser.toUpperCase()
      return ba?.name?.toUpperCase()?.indexOf(term) !== -1
    }

    function getRoleOptions(accessor) {
      // CareBrain only: unlike Invite/Share, Care Manager can be assigned from this table.
      const options = getAvailableRoles(accessor.botId)
      if (!options.length || isQcs) return options
      return [...options, ROLE_OPTIONS.find(role => role.value === ROLE.MANAGER)]
    }

    function renderDeleteAccess(accessor, adminUserId, removeAccessFn) {
      // Care Managers cannot be removed here. Archived QCS rows cannot either.
      if (accessor.role === ROLE.MANAGER) return null
      if (isQcs && accessor.archived === true) return null
      if (accessor.adminId !== adminUserId) {
        return (
          <div className={classes.tableCellData}>
            <DeleteAccess
              email={accessor.email}
              id={accessor.id}
              inviteAccessId={accessor.inviteAccessId}
              submit={removeAccessFn}
            />
          </div>
        )
      }
      return null
    }

    function renderAccessorName(accessor) {
      const isAdminRole = accessor.role === ROLE.ADMIN || accessor.role === ROLE.CARECO_ADMIN
      // Admin / Careco Admin rows never show the invite state.
      const showPendingInvite = accessor.isInvited && !isAdminRole
      if (showPendingInvite) {
        return (
          <p className={classes.tableCellData}>
            {accessor.name}
            <i className={classes.pendingInvite}>Pending invitation</i>
          </p>
        )
      }
      // Invited Admin / Careco Admin: keep the plain name but in italics.
      if (accessor.isInvited && isAdminRole) {
        return (
          <p className={classes.tableCellData}>
            <i>{accessor.name}</i>
          </p>
        )
      }
      return <p className={classes.tableCellData}>{accessor.name}</p>
    }

    return (
      <div style={{ marginBottom: '50px' }}>
        <Heading>Platform Admins</Heading>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 20,
          }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div className={classes.inputHolder}>
              <input
                className={classes.input}
                placeholder="Search admin panel users by name"
                onChange={event => this.handleSearchUser(event.target.value)}
              />
              <SearchIcon width="20" height="20" color="var(--color-mischka-grey)" />
            </div>
          </div>
          {canShareBot && !isQcs && (
            <Button className={classes.submitButton} onClick={() => this.setState({ open: true })}>
              <p className={classes.submitButtonText}>Invite new admin</p>
            </Button>
          )}
        </div>
        <Table titles={titles}>
          {this.state.accessorsLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
              <Loader />
            </div>
          ) : !shouldDisplayTableContents ? (
            <div />
          ) : (
            botAccessors
              .sort(compareBotAccessors)
              .filter(ba => isMatchingSearch(ba, this.state.searchUser))
              .map((accessor, index) => (
                <TableRow
                  key={uuid()}
                  index={index}
                  style={
                    isQcs && accessor.archived === true
                      ? { opacity: 0.55, pointerEvents: 'none' }
                      : undefined
                  }>
                  <TableCell
                    style={{
                      maxWidth: 210,
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                    <div className={classes.userImageContainer}>
                      {isQcs && !accessor?.imageUrl ? (
                        <DefaultUserIcon className={classes.userImage} />
                      ) : (
                        <img
                          className={classes.userImage}
                          src={accessor?.imageUrl || baseUserImage}
                          alt="user image"
                        />
                      )}
                      <span
                        className={classes.userStatus}
                        style={{
                          background: getUserStatusColor(accessor),
                        }}
                      />
                    </div>
                    {renderAccessorName(accessor)}
                  </TableCell>
                  <TableCell style={{ maxWidth: 210 }}>
                    <p
                      className={classes.tableCellData}
                      style={{
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        width: '100%',
                        whiteSpace: 'nowrap',
                      }}
                      title={accessor.email}>
                      {accessor.email}
                    </p>
                  </TableCell>
                  <TableCell style={{ maxWidth: 150 }}>
                    <div className={classes.tableCellData} style={{ width: '100%' }}>
                      {this.state.updatingAccessorKey != null &&
                      this.state.updatingAccessorKey === (accessor.id != null ? accessor.id : accessor.inviteAccessId) ? (
                          <Loader size={{ circleSize: 24, border: 3 }} />
                        ) : (
                          <Select
                            styles={customStyles}
                            options={getRoleOptions(accessor)}
                            value={ROLE_OPTIONS.find(access => access.value === accessor.role)}
                            name="selected-state"
                            clearable={false}
                            isDisabled={Boolean(accessor.isInvited) || (isQcs && accessor.archived === true)}
                            onChange={o => this.updateRole(accessor.id, accessor.inviteAccessId, o.value, accessor)}
                            searchable={true}
                          />
                        )}
                    </div>
                  </TableCell>
                  {showSendNotifications && (
                    <TableCell
                      style={{
                        maxWidth: 100,
                        display: 'flex',
                        justifyContent: 'center',
                      }}>
                      <div>
                        {accessor.isInvited == false ? (
                          <div className={classes.tableCellData}>
                            <FormControlLabel
                              className="checkbox"
                              control={
                                <Checkbox
                                  style={{
                                    color: 'var(--color-button-secondary)',
                                  }}
                                  onChange={(event, checked) => this.updateSupportNotifying(accessor.id, checked)}
                                  checked={accessor.sendEmailNotifications}
                                  color="primary"
                                />
                              }
                            />
                          </div>
                        ) : null}
                      </div>
                    </TableCell>
                  )}
                  {!isQcs && (
                    <TableCell>{renderDeleteAccess(accessor, this.props.adminUser && this.props.adminUser.id, this.removeAccess)}</TableCell>
                  )}
                </TableRow>
              ))
          )}
        </Table>
        {!isQcs && (
          <Modal
            classes={{ paper: classes.paper }}
            open={this.state.open}
            onClose={this.closeShareModal}
            title="Share a chatbot">
            <ShareModal bot={{ id: this.props.match.params.botId }} closeModal={this.closeShareModal} />
          </Modal>
        )}
      </div>
    )
  }
}

const mapStateToProps = state => ({
  botAccessors: state.botAccessors,
  adminUser: state.adminUser,
  activeBot: state.activeBot,
})

export default withRouter(withStyles(styles, { withTheme: true })(connect(mapStateToProps)(Users)))
